/**
 * Developer Portal — Personal Access Tokens
 * Feb 2026
 *
 * Users create scoped, revocable API tokens (`acq_pat_...`) to build
 * their own mobile / desktop / integration clients against the
 * Acquilix API.
 *
 * Security notes for future readers:
 *   - Raw token is displayed ONLY once (post-create modal). Never stored
 *     client-side, never fetched again.
 *   - Every token is scoped + workspace-pinned server-side.
 *   - Revocation is instant.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { KeyRound, Trash2, Copy, ShieldCheck, ShieldAlert, Plus, Code2, ExternalLink, RefreshCw } from "lucide-react";

const AXIOS_BASE = (api.defaults?.baseURL || "").replace(/\/$/, "");
// Feb 2026 · Prefer the browser's own origin for docs strings so
// preview vs. production always render the URL the user is actually
// viewing (avoids the "prod docs point at preview" bug when the
// frontend was built with a mismatched REACT_APP_BACKEND_URL).
const DOCS_BASE = (() => {
  try { return `${window.location.origin}/api/v1`; }
  catch { return AXIOS_BASE; }
})();

export default function DeveloperSettings() {
  const { activeWorkspaceId } = useAuth();
  const [scopeGroups, setScopeGroups] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [freshToken, setFreshToken] = useState(null);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get(`/developer/scopes`),
        api.get(`/developer/tokens`),
      ]);
      setScopeGroups(s.data?.groups || []);
      setTokens(t.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load developer settings")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this token? Any app using it will stop working immediately.")) return;
    try {
      await api.delete(`/developer/tokens/${id}`);
      toast.success("Token revoked");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Revoke failed")); }
  };

  const rotate = async (id) => {
    if (!window.confirm("Rotate this token? The old token stops working immediately — you'll see the new one exactly once.")) return;
    try {
      const { data } = await api.post(`/developer/tokens/${id}/rotate`);
      setFreshToken({ ...data, name: (tokens.find((t) => t.id === id) || {}).name || "Rotated token" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Rotate failed")); }
  };

  return (
    <div className="pb-8" data-testid="developer-settings-page">
      <PageHeader
        icon={KeyRound}
        title="Developer"
        subtitle="Generate API tokens to build custom apps and integrations on top of Acquilix."
      />
      <div className="px-4 sm:px-6 md:px-10 space-y-6">
        {/* Feb 2026 · Sub-nav for the Developer section. Tokens is the
            landing tab; Webhooks + API Docs are sibling pages. */}
        <div className="flex items-center gap-4 border-b border-border pb-1"
             data-testid="developer-subnav">
          <a href="/app/developer"
             className="px-3 py-1.5 text-sm font-medium border-b-2 border-primary text-foreground"
             data-testid="dev-subnav-tokens">Tokens</a>
          <a href="/app/developer/webhooks"
             className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
             data-testid="dev-subnav-webhooks">Webhooks</a>
          <a href="/app/developer/docs"
             className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
             data-testid="dev-subnav-docs">API Docs</a>
        </div>

        {/* Intro card */}
        <div className="border border-primary/40 bg-primary/5 p-5 flex items-start gap-4"
             data-testid="dev-intro-card">
          <ShieldCheck className="w-6 h-6 text-primary shrink-0 mt-0.5" />
          <div className="space-y-1 flex-1">
            <div className="label-eyebrow">Personal Access Tokens</div>
            <p className="text-sm text-muted-foreground">
              Tokens act on <span className="font-semibold text-foreground">your behalf</span> and inherit your workspace permissions,
              limited to the exact scopes you pick. Ideal for CI, integrations,
              or a companion mobile / desktop app of your own. Treat every token
              like a password — never commit them to git or paste them into chat.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="dev-create-token-btn"
                   className="rounded-none whitespace-nowrap">
            <Plus className="w-4 h-4 mr-1.5" /> Generate token
          </Button>
        </div>

        {/* Tokens table */}
        <TokenTable tokens={tokens} onRevoke={revoke} onRotate={rotate} />

        {/* Docs strip */}
        <ApiDocs baseUrl={DOCS_BASE} />
      </div>

      {showCreate && (
        <CreateTokenDialog
          scopeGroups={scopeGroups}
          workspaceId={activeWorkspaceId}
          onClose={() => setShowCreate(false)}
          onCreated={(doc) => { setShowCreate(false); setFreshToken(doc); load(); }}
        />
      )}

      {freshToken && (
        <FreshTokenDialog doc={freshToken} onClose={() => setFreshToken(null)} baseUrl={DOCS_BASE} />
      )}
    </div>
  );
}

// ─── Tokens list ────────────────────────────────────────────────────────
function TokenTable({ tokens, onRevoke, onRotate }) {
  if (!tokens.length) {
    return (
      <div className="border border-dashed border-border p-10 text-center"
           data-testid="dev-empty-state">
        <KeyRound className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <div className="font-semibold">No tokens yet</div>
        <p className="text-sm text-muted-foreground mt-1">
          Click <span className="font-semibold text-foreground">Generate token</span> to create your first API key.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-border" data-testid="dev-tokens-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest bg-muted/20 text-muted-foreground">
            <th className="text-left px-3 py-2 font-semibold">Name</th>
            <th className="text-left px-3 py-2 font-semibold">Prefix</th>
            <th className="text-left px-3 py-2 font-semibold">Scopes</th>
            <th className="text-left px-3 py-2 font-semibold w-40">Rate limit</th>
            <th className="text-left px-3 py-2 font-semibold w-40">Last used</th>
            <th className="text-left px-3 py-2 font-semibold w-32">Expires</th>
            <th className="text-left px-3 py-2 font-semibold w-24">Status</th>
            <th className="w-14"></th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((t) => {
            const revoked = !!t.revoked_at;
            const expired = t.expires_at && new Date(t.expires_at) < new Date();
            return (
              <tr key={t.id} className="border-t border-border align-top"
                  data-testid={`dev-token-row-${t.id}`}>
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{t.prefix}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(t.scopes || []).map((s) => (
                      <span key={s} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5">
                        {s}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">{t.rate_limit_per_min || 120}/min</td>
                <td className="px-3 py-2 text-xs">
                  {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 text-xs">
                  {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : "Never"}
                </td>
                <td className="px-3 py-2 text-xs">
                  {revoked ? <span className="text-red-600">Revoked</span>
                    : expired ? <span className="text-orange-600">Expired</span>
                    : <span className="text-emerald-600">Active</span>}
                </td>
                <td className="px-3 py-2">
                  {!revoked && (
                    <>
                      <button onClick={() => onRotate(t.id)}
                              className="text-primary hover:bg-primary/10 p-1 mr-1"
                              title="Rotate token"
                              data-testid={`dev-rotate-${t.id}`}>
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button onClick={() => onRevoke(t.id)}
                              className="text-red-600 hover:bg-red-50 p-1"
                              title="Revoke token"
                              data-testid={`dev-revoke-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Create dialog ──────────────────────────────────────────────────────
function CreateTokenDialog({ scopeGroups, workspaceId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [expiryDays, setExpiryDays] = useState("");
  const [rate, setRate] = useState(120);
  const [cidrs, setCidrs] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const toggle = (k) => {
    const s = new Set(selected);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelected(s);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!selected.size) { toast.error("Pick at least one scope"); return; }
    setSaving(true);
    try {
      const cleanCidrs = cidrs.split(",").map((c) => c.trim()).filter(Boolean);
      const payload = {
        name: name.trim(),
        workspace_id: workspaceId,
        scopes: Array.from(selected),
        rate_limit_per_min: parseInt(rate, 10) || 120,
        allowed_cidrs: cleanCidrs,
      };
      const days = parseInt(expiryDays, 10);
      if (days > 0) payload.expires_in_days = days;
      const { data } = await api.post(`/developer/tokens`, payload);
      onCreated(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="dev-create-dialog">
        <DialogHeader>
          <DialogTitle>Generate a new API token</DialogTitle>
          <DialogDescription>
            Choose the smallest set of scopes your integration needs. You can always create another token later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. My mobile app"
                      className="rounded-none mt-1"
                      data-testid="dev-create-name" />
            </div>
            <div>
              <Label className="label-eyebrow">Expiry</Label>
              <select value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)}
                       className="w-full h-9 border border-border rounded-none mt-1 text-sm bg-background px-2"
                       data-testid="dev-create-expiry">
                <option value="">Never</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="365">1 year</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Scopes</Label>
            <div className="mt-1 border border-border p-3 max-h-72 overflow-y-auto space-y-3"
                  data-testid="dev-create-scopes">
              {scopeGroups.map((g) => (
                <div key={g.group}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {g.group}
                  </div>
                  <div className="mt-1 space-y-1">
                    {g.scopes.map((s) => (
                      <label key={s.key} className="flex items-start gap-2 text-sm cursor-pointer">
                        <input type="checkbox"
                                checked={selected.has(s.key)}
                                onChange={() => toggle(s.key)}
                                className="mt-0.5"
                                data-testid={`dev-scope-${s.key}`} />
                        <span>
                          <span className="font-mono text-xs">{s.key}</span>
                          <span className="text-muted-foreground"> — {s.label}</span>
                          {s.desc && <div className="text-[11px] text-muted-foreground/80">{s.desc}</div>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Rate limit (per minute)</Label>
              <Input type="number" min={10} max={6000} value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      className="rounded-none mt-1"
                      data-testid="dev-create-rate" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Requests above the limit get a 429 with retry-after.
              </p>
            </div>
            <div>
              <Label className="label-eyebrow">IP allow-list (optional)</Label>
              <Input value={cidrs} onChange={(e) => setCidrs(e.target.value)}
                      placeholder="e.g. 203.0.113.0/24, 198.51.100.5/32"
                      className="rounded-none mt-1"
                      data-testid="dev-create-cidrs" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Comma-separated CIDRs. Empty = allow every IP.
              </p>
            </div>
          </div>

          <div className="border-l-2 border-amber-500 pl-3 py-1 text-xs bg-amber-500/5">
            <ShieldAlert className="w-3.5 h-3.5 inline mr-1 text-amber-600" />
            The generated token will be shown <span className="font-semibold">once</span>. Store it in a
            secret manager immediately — you won't be able to view it again.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-none"
                   data-testid="dev-create-submit">
            {saving ? "Generating…" : "Generate token"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Post-creation "copy your token" dialog ─────────────────────────────
function FreshTokenDialog({ doc, onClose, baseUrl }) {
  const tokenRef = useRef(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(doc.token);
      toast.success("Token copied to clipboard");
    } catch { toast.error("Copy failed — select the text manually"); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="dev-fresh-token-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> Copy your token now
          </DialogTitle>
          <DialogDescription>
            This is the only time <span className="font-semibold">{doc.name}</span> will be shown.
            Store it in a password manager or your CI secret store.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border bg-muted/30 p-3 font-mono text-xs break-all"
                ref={tokenRef} data-testid="dev-fresh-token-value">
            {doc.token}
          </div>
          <Button onClick={copy} className="rounded-none" data-testid="dev-fresh-token-copy">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy token
          </Button>
          <div className="text-xs mt-4">
            <div className="label-eyebrow mb-1">Try it</div>
            <pre className="border border-border p-3 bg-muted/20 text-[11px] overflow-x-auto whitespace-pre-wrap"
                  data-testid="dev-fresh-token-curl">
{`curl "${baseUrl}/auth/me" \\
  -H "Authorization: Bearer ${doc.token}"`}
            </pre>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-none" data-testid="dev-fresh-token-done">
            I've saved the token
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Compact API docs strip ─────────────────────────────────────────────
function ApiDocs({ baseUrl }) {
  return (
    <div className="border border-border p-5" data-testid="dev-docs">
      <div className="flex items-center gap-2 mb-3">
        <Code2 className="w-4 h-4 text-primary" />
        <div className="label-eyebrow">Quick start</div>
      </div>
      <div className="grid md:grid-cols-3 gap-3 text-xs">
        <CodeCard title="curl" testid="dev-doc-curl">
{`curl "${baseUrl}/workspaces/<ws-id>/tasks" \\
  -H "Authorization: Bearer $ACQ_PAT"`}
        </CodeCard>
        <CodeCard title="JavaScript / fetch" testid="dev-doc-js">
{`const r = await fetch(
  "${baseUrl}/workspaces/<ws-id>/tasks",
  { headers: { Authorization:
    "Bearer " + process.env.ACQ_PAT } }
);
const tasks = await r.json();`}
        </CodeCard>
        <CodeCard title="Python / requests" testid="dev-doc-py">
{`import os, requests
r = requests.get(
  "${baseUrl}/workspaces/<ws-id>/tasks",
  headers={"Authorization":
    f"Bearer {os.environ['ACQ_PAT']}"})
tasks = r.json()`}
        </CodeCard>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        Base URL: <code>{baseUrl}</code>. Every route is versioned under <code>/api/v1</code>.
        Full endpoint reference is in <a href="/app/memory/MOBILE_HANDOFF.md" className="underline text-primary inline-flex items-center gap-1">
          the Mobile handoff kit <ExternalLink className="w-3 h-3" />
        </a>.
      </p>
    </div>
  );
}

function CodeCard({ title, children, testid }) {
  return (
    <div className="border border-border">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/20 px-2 py-1">
        {title}
      </div>
      <pre className="p-2 text-[11px] overflow-x-auto whitespace-pre-wrap" data-testid={testid}>{children}</pre>
    </div>
  );
}
